import type { FC } from "react";
import React, { useMemo } from "react";

import { extractProps, getChildrenRecord } from "../../../utils/react";
import { ButtonCellSchevron } from "../ButtonCellSchevron";

import type { ButtonCellProps } from "..";

export const BUTTON_CELL_SCHEVRON_OPTION_DISPLAY_NAME =
    "ButtonCellSchevronOption";

export const BUTTON_CELL_SCHEVRON_OPTION_TEST_ID =
    "button-cell-schevron-option";

/**
 * Компонент кнопки, который отображает доступное действие в виде горизонтальной ячейки.
 *
 * Основан на компоненте ButtonCellSchevron и переиспользует динамические отступы, область нажатия и все состояния.
 *
 * По умолчанию отображает contentsView, leftIcon, rightIcon и bottomAddon. LeftIcon можно заменять, rightIcon остается статичным - иконка шеврона "Перейти".
 *
 * В качестве bottomAddon предустановлены 2 ButtonInline с разделителем в виде компонента Divider.
 *
 * <a href="https://www.figma.com/design/64y1aDfl0lSSpek3sq1Lrt/%F0%9F%8D%AC-Cards-%26-Cells-%26-Views?node-id=4220-52506&m=dev" target="_blank">Спека</a>
 * @constructor
 */
export const ButtonCellSchevronOption: FC<ButtonCellProps> = (props) => {
    const {
        customVarsStyles,
        currentProps: {
            state = "enabled",
            children,
            "data-tsid": tsid = BUTTON_CELL_SCHEVRON_OPTION_TEST_ID,
            ...restProps
        },
    } = useMemo(() => extractProps(props, ["customVarsStyles"]), [props]);

    const { Label, Subtitle, Value, LeftIcon, BottomAddon } = useMemo(
        () =>
            getChildrenRecord(
                children,
                BUTTON_CELL_SCHEVRON_OPTION_DISPLAY_NAME,
                {
                    Label: {
                        required: true,
                    },
                    Unknown: {
                        skipElement: true,
                    },
                },
            ),
        [children],
    );

    return (
        <ButtonCellSchevron
            {...restProps}
            state={state}
            customVarsStyles={customVarsStyles}
            data-tsid={tsid}
            data-uikit={BUTTON_CELL_SCHEVRON_OPTION_DISPLAY_NAME}
        >
            <ButtonCellSchevron.Label>{Label}</ButtonCellSchevron.Label>

            {Subtitle && (
                <ButtonCellSchevron.Subtitle>
                    {Subtitle}
                </ButtonCellSchevron.Subtitle>
            )}

            {Value && (
                <ButtonCellSchevron.Value>{Value}</ButtonCellSchevron.Value>
            )}

            {LeftIcon && <ButtonCellSchevron.LeftIcon {...LeftIcon.props} />}

            {BottomAddon && (
                <ButtonCellSchevron.BottomAddon>
                    {BottomAddon}
                </ButtonCellSchevron.BottomAddon>
            )}
        </ButtonCellSchevron>
    );
};

ButtonCellSchevronOption.displayName = BUTTON_CELL_SCHEVRON_OPTION_DISPLAY_NAME;

export default ButtonCellSchevronOption;
